import React, { useCallback } from 'react';
import { cn } from '@ais-ui/utils';
import { PiArrowSquareOut, PiBoat, PiPath } from 'react-icons/pi';
import { Port } from '../AISMapContext';
import { usePermissions } from '../../Permissions/PermissionsProvider';
import {
  PortSelectionMode,
  useSelectionState,
} from '../../../components/SelectionStateProvider';
import { useMapContextMenu } from './MapContextMenuContext';
import { openPopupOnSecondaryScreen, PopupType } from '@ais-ui/popups';

interface PortContextMenuItemsProps {
  port: Port;
}

interface MenuItemProps {
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

const MenuItem = ({ disabled, onClick, children }: MenuItemProps) => (
  <div
    className={cn(
      'relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none',
      disabled
        ? 'pointer-events-none opacity-50'
        : 'hover:bg-accent hover:text-accent-foreground cursor-pointer',
    )}
    onClick={disabled ? undefined : onClick}
  >
    {children}
  </div>
);

export const PortContextMenuItems = ({ port }: PortContextMenuItemsProps) => {
  const permissions = usePermissions();
  const { setSelectedPorts, setPortSelectionMode } = useSelectionState();
  const { closeContextMenu } = useMapContextMenu();

  const selectPort = useCallback(
    (mode: PortSelectionMode) => {
      setPortSelectionMode(mode);
      setSelectedPorts([port]);
      closeContextMenu();
    },
    [port, setPortSelectionMode, setSelectedPorts, closeContextMenu],
  );

  const openPopup = useCallback(() => {
    const params: Record<string, string | number> = {};
    if (port.id) params.portId = Number(port.id);
    if (port.name) params.portName = port.name;
    if (port.locode) params.portLocode = port.locode;
    
    openPopupOnSecondaryScreen(PopupType.Routes, params);
    closeContextMenu();
  }, [port, closeContextMenu]);

  return (
    <>
      <MenuItem
        disabled={!permissions.port}
        onClick={() => selectPort('routes')}
      >
        <PiPath className="h-4 w-4" />
        Show Routes
      </MenuItem>

      <MenuItem
        disabled={!permissions.nextport}
        onClick={() => selectPort('nextport')}
      >
        <PiBoat className="h-4 w-4" />
        Show NextPort Arrivals
      </MenuItem>

      <div className="-mx-1 my-1 h-px bg-border" />

      <MenuItem
        disabled={!permissions.port}
        onClick={openPopup}
      >
        <PiArrowSquareOut className="h-4 w-4" />
        Open Popup
      </MenuItem>
    </>
  );
};
